// frontend/app/components/lessonPlans/authoring/BlockAnswerFields.js
//
// The two field kinds that carry an answer key: multiple-choice style options,
// and ordering items whose authored order IS the key. Split out of BlockEditor
// so that file stays a dispatcher over blockSchemas rather than a form.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { IconButton, TextField } from "./WizardFields";

export const nextOptionId = (options = []) => {
  const used = new Set(options.map((o) => o.id));
  for (let i = 0; i < 26; i += 1) {
    const id = String.fromCharCode(97 + i);
    if (!used.has(id)) return id;
  }
  return `o${options.length + 1}`;
};

/** Answer options, with the correct one(s) marked inline. */
export const OptionsField = ({ colors, block, field, onPatch, multi, t }) => {
  const translate = t || ((key) => key);
  const options = block[field.key] || [];
  const correctIds = multi
    ? block.correct_option_ids || []
    : block.correct_option_id
      ? [block.correct_option_id]
      : [];

  const setOptions = (next) => onPatch({ [field.key]: next });

  const toggleCorrect = (id) => {
    if (multi) {
      const next = correctIds.includes(id)
        ? correctIds.filter((x) => x !== id)
        : [...correctIds, id];
      onPatch({ correct_option_ids: next });
    } else {
      onPatch({ correct_option_id: id });
    }
  };

  const removeOption = (id) => {
    setOptions(options.filter((o) => o.id !== id));
    // Drop the answer too, or the block keeps pointing at an option that is
    // no longer on screen and silently becomes unanswerable.
    if (multi) {
      onPatch({ correct_option_ids: correctIds.filter((x) => x !== id) });
    } else if (block.correct_option_id === id) {
      onPatch({ correct_option_id: null });
    }
  };

  return (
    <View>
      {options.map((option, index) => {
        const isCorrect = correctIds.includes(option.id);
        return (
          <View key={option.id} style={styles.optionRow}>
            <TouchableOpacity
              onPress={() => toggleCorrect(option.id)}
              accessibilityRole={multi ? "checkbox" : "radio"}
              accessibilityState={{ checked: isCorrect }}
              accessibilityLabel={`${translate("Mark correct")} ${option.id.toUpperCase()}`}
              style={[
                styles.correctToggle,
                {
                  borderColor: isCorrect ? colors.success : colors.borderStrong,
                  backgroundColor: isCorrect ? colors.success : "transparent",
                  borderRadius: multi ? 6 : 13,
                },
              ]}
            >
              {isCorrect ? (
                <MaterialCommunityIcons name="check" size={14} color="#ffffff" />
              ) : (
                <Text style={[styles.optionLetter, { color: colors.textMuted }]}>
                  {option.id.toUpperCase()}
                </Text>
              )}
            </TouchableOpacity>

            <View style={{ flex: 1 }}>
              <TextField
                colors={colors}
                value={option.text}
                onChange={(text) =>
                  setOptions(
                    options.map((o) => (o.id === option.id ? { ...o, text } : o))
                  )
                }
                placeholder={`${translate("Answer")} ${index + 1}`}
              />
            </View>

            <IconButton
              colors={colors}
              icon="close"
              label={`${translate("Remove answer")} ${index + 1}`}
              danger
              disabled={options.length <= 2}
              onPress={() => removeOption(option.id)}
            />
          </View>
        );
      })}

      <TouchableOpacity
        onPress={() =>
          setOptions([...options, { id: nextOptionId(options), text: "" }])
        }
        accessibilityRole="button"
        accessibilityLabel={translate("Add answer")}
        style={[styles.addRow, { borderColor: colors.borderStrong }]}
      >
        <MaterialCommunityIcons name="plus" size={14} color={colors.accentPrimary} />
        <Text style={[styles.addText, { color: colors.accentPrimary }]}>
          {translate("Add answer")}
        </Text>
      </TouchableOpacity>

      <Text style={[styles.hint, { color: colors.textMuted }]}>
        {multi
          ? translate("Tick every correct answer.")
          : translate("Tick the correct answer.")}
      </Text>
    </View>
  );
};

/** Ordering items — the authored order IS the answer, so it is kept in sync. */
export const OrderItemsField = ({ colors, block, field, onPatch, t }) => {
  const translate = t || ((key) => key);
  const items = block[field.key] || [];

  const commit = (next) =>
    onPatch({ [field.key]: next, correct_order: next.map((item) => item.id) });

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    next[index] = items[target];
    next[target] = items[index];
    commit(next);
  };

  return (
    <View>
      {items.map((item, index) => (
        <View key={item.id} style={styles.optionRow}>
          <View style={[styles.orderIndex, { backgroundColor: colors.accentGlow }]}>
            <Text style={[styles.orderIndexText, { color: colors.accentPrimary }]}>
              {index + 1}
            </Text>
          </View>

          <View style={{ flex: 1 }}>
            <TextField
              colors={colors}
              value={item.text}
              onChange={(text) =>
                commit(items.map((o) => (o.id === item.id ? { ...o, text } : o)))
              }
              placeholder={`${translate("Step")} ${index + 1}`}
            />
          </View>

          <IconButton
            colors={colors}
            icon="chevron-up"
            label={translate("Move up")}
            disabled={index === 0}
            onPress={() => move(index, -1)}
          />
          <IconButton
            colors={colors}
            icon="chevron-down"
            label={translate("Move down")}
            disabled={index === items.length - 1}
            onPress={() => move(index, 1)}
          />
          <IconButton
            colors={colors}
            icon="close"
            label={`${translate("Remove step")} ${index + 1}`}
            danger
            disabled={items.length <= 2}
            onPress={() => commit(items.filter((o) => o.id !== item.id))}
          />
        </View>
      ))}

      <TouchableOpacity
        onPress={() => commit([...items, { id: nextOptionId(items), text: "" }])}
        accessibilityRole="button"
        accessibilityLabel={translate("Add step")}
        style={[styles.addRow, { borderColor: colors.borderStrong }]}
      >
        <MaterialCommunityIcons name="plus" size={14} color={colors.accentPrimary} />
        <Text style={[styles.addText, { color: colors.accentPrimary }]}>
          {translate("Add step")}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  optionRow: { flexDirection: "row", alignItems: "flex-start", gap: 7, marginBottom: 8 },
  correctToggle: {
    width: 26,
    height: 26,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 7,
  },
  optionLetter: { fontSize: 11, fontWeight: "800" },
  orderIndex: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 7,
  },
  orderIndexText: { fontSize: 11, fontWeight: "800" },

  addRow: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 9,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 10,
    paddingVertical: 7,
    gap: 5,
  },
  addText: { fontSize: 12, fontWeight: "700" },
  hint: { fontSize: 11, marginTop: 6 },
});
